/** Metadatos de salida de zonas espaciales (espejo de server/spatialZoneAnalysis.js). */

export const SPATIAL_ZONE_OUTPUT_META = {
  fields: ['zone_id', 'centroid_x', 'centroid_y', 'eventos'],
  optionalFields: ['periodo'],
  numeric: ['centroid_x', 'centroid_y', 'eventos'],
  defaultSort: { field: 'eventos', dir: 'desc' },
};

export const SPATIAL_ZONE_FILTER_OPERATORS = [
  { value: 'eq', label: 'Igual a' },
  { value: 'neq', label: 'Distinto de' },
  { value: 'gt', label: 'Mayor que' },
  { value: 'gte', label: 'Mayor o igual' },
  { value: 'lt', label: 'Menor que' },
  { value: 'lte', label: 'Menor o igual' },
  { value: 'contains', label: 'Contiene' },
  { value: 'ncontains', label: 'No contiene' },
];

function isValueCol(c) {
  return (
    c?.role === 'value' ||
    c?.fieldRole === 'value' ||
    c?.jsonMode === 'arrayCount' ||
    c?.jsonMode === 'arraySum' ||
    (c?.aggregation && c.aggregation !== 'none')
  );
}

function isUnnestCol(c) {
  return c?.jsonMode === 'arrayUnnest' || c?.jsonMode === 'array_unnest';
}

function colBaseName(c) {
  const alias = String(c?.alias ?? '').trim();
  return alias || String(c?.column || '');
}

function isLikelyTimeColumn(c) {
  const col = String(c?.column || '').toLowerCase();
  const alias = String(c?.alias ?? '').trim().toLowerCase();
  if (
    col === 'time' ||
    col === 'timestamp' ||
    col === 'timestamptz' ||
    col.includes('fecha') ||
    col.endsWith('_at') ||
    col.endsWith('_time')
  ) {
    return true;
  }
  if (
    alias === 'time' ||
    alias === 'timestamp' ||
    alias.includes('fecha') ||
    alias.endsWith('_at') ||
    alias.endsWith('_time')
  ) {
    return true;
  }
  const bucket = c?.dateBucket;
  if (
    bucket &&
    bucket !== 'dayOfWeek' &&
    bucket !== 'hourOfDay' &&
    bucket !== 'weekOfYear'
  ) {
    return true;
  }
  return false;
}

function pickTimeColumnIndex(columns) {
  const cols = columns || [];
  const candidates = cols
    .map((c, i) => ({ c, i }))
    .filter(({ c }) => c?.tableId && c?.column && !isValueCol(c) && !isUnnestCol(c));

  const timeLike = candidates.filter(({ c }) => isLikelyTimeColumn(c));
  const pool = timeLike.length ? timeLike : candidates;

  const explicit = pool.find(({ c }) => c.dateBucket === 'minute' || c.dateBucket === 'hour');
  if (explicit) return explicit.i;
  const raw = pool.find(({ c }) => c.dateBucket === 'raw' || !c.dateBucket);
  if (raw) return raw.i;
  const day = pool.find(({ c }) => c.dateBucket === 'day');
  if (day) return day.i;
  return pool[0] ? pool[0].i : -1;
}

export function resolveSpatialGroupCols(columns) {
  const cols = columns || [];
  const timeIdx = pickTimeColumnIndex(cols);
  const groups = [];
  cols.forEach((c, i) => {
    if (!c?.tableId || !c?.column) return;
    if (isValueCol(c) || isUnnestCol(c) || i === timeIdx) return;
    if (isLikelyTimeColumn(c)) return;
    const base = colBaseName(c);
    groups.push({ index: i, base });
  });
  return groups;
}

export function spatialZoneOutputFields(spatialZoneAnalysis, groupColumns = []) {
  const labels = spatialZoneAnalysis?.columnLabels || {};
  const includePeriodo = Boolean(spatialZoneAnalysis?.timeBucket);
  const used = new Set();
  const uniquify = (wanted) => {
    let candidate = wanted;
    let n = 2;
    while (used.has(candidate)) {
      candidate = `${wanted}_${n}`;
      n += 1;
    }
    used.add(candidate);
    return candidate;
  };

  const groupFields = (groupColumns || []).map((g) => {
    const base = g.base;
    const value = uniquify(String(labels[base] ?? '').trim() || base);
    return { base, value, group: true };
  });

  const zoneFields = [];
  if (includePeriodo) {
    const base = 'periodo';
    zoneFields.push({
      base,
      value: uniquify(String(labels[base] ?? '').trim() || base),
      numeric: false,
    });
  }
  for (const base of SPATIAL_ZONE_OUTPUT_META.fields) {
    zoneFields.push({
      base,
      value: uniquify(String(labels[base] ?? '').trim() || base),
      numeric: SPATIAL_ZONE_OUTPUT_META.numeric.includes(base),
    });
  }

  return [...groupFields, ...zoneFields];
}

export function spatialZoneOutputDescription() {
  return 'zonas agregadas por proximidad espacial';
}

export function hasUnnestDatasetColumn(columns) {
  return (columns || []).some(isUnnestCol);
}
